import {
  Avatar,
  Badge,
  Button,
  IconButton,
  Menu,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import { CloseRounded, InboxOutlined } from '@material-ui/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RRLink, useHistory } from 'react-router-dom';
import { GetMyTask } from '../api/project-document-workflows';
import { RootState } from '../app/store';
import { useAppDispatch } from '../hooks/useAppRudux';
import { getBellNotificationActionItemsAsync } from '../pages/BellNotification/bellNotificationSlide';
import backspaceLogo from './../assets/backspacelogo64.png';

const ACTION_LIMIT = 5;

export const bellNotificationActionItemParams: GetMyTask = {
  page: 0,
  limit: ACTION_LIMIT,
  today: new Date()
};

export default function BellNotification() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { location } = useHistory();
  const { t } = useTranslation();

  // Add condition for replacing URL when navigating to other page
  const replace = location.pathname.startsWith('/approvals');

  const { actionItems } = useSelector(
    (state: RootState) => state.bellNotifications
  );

  const [anchorNotification, setAnchorNotification] =
    useState<null | HTMLElement>(null);
  const openNotification = Boolean(anchorNotification);
  const countAllNotification = actionItems.count;

  useEffect(() => {
    dispatch(
      getBellNotificationActionItemsAsync(bellNotificationActionItemParams)
    );
  }, []);

  function handleNotification(open: boolean) {
    return (event: React.MouseEvent<HTMLElement>) => {
      if (open)
        dispatch(
          getBellNotificationActionItemsAsync(bellNotificationActionItemParams)
        );
      setAnchorNotification(open ? event.currentTarget : null);
    };
  }

  return (
    <>
      <Tooltip title={t('notifications.title') as string}>
        <IconButton
          className={classes.notiButton}
          color="inherit"
          aria-label={t('notifications.title') as string}
          onClick={handleNotification(!openNotification)}
        >
          <Badge
            badgeContent={countAllNotification}
            color="secondary"
            invisible={countAllNotification === 0 || openNotification}
          >
            <InboxOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="notification"
        anchorEl={anchorNotification}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openNotification}
        onClose={handleNotification(false)}
        PaperProps={{
          style: {
            maxHeight: 480,
            width: 400,
            borderRadius: 16
          }
        }}
      >
        <div className={classes.notiHeader}>
          <Typography className={classes.flexGrow} variant="h6" gutterBottom>
            {t('notifications.title')}
          </Typography>
          <Tooltip title={t('common.close') as string}>
            <IconButton size="small" onClick={handleNotification(false)}>
              <CloseRounded />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.notiHeader}>
          {actionItems.count > 0 && (
            <div className={classes.countBadge}>{actionItems.count}</div>
          )}
          <Typography className={classes.flexGrow} variant="subtitle2">
            {t('notifications.waitForReview')}
          </Typography>
        </div>
        {actionItems.data.length > 0 ? (
          actionItems.data.map((actionItem, index) => {
            const primaryText = t('notifications.assignedDocument', {
              documentName: actionItem.projectDocumentRevision.projectDocument.name,
              revisionName: actionItem.projectDocumentRevision.revision.name,
              projectName: actionItem.projectDocumentRevision.projectDocument.project.name
            });
            const secondaryText = t('notifications.dueDate', {
              date: moment(actionItem.currentWorkflowStep.dueDate).format('ll')
            });
            const to = `/approvals/${actionItem.id}`;
            return (
              <RRLink
                to={to}
                replace={replace}
                className={classes.notiItem}
                onClick={handleNotification(false)}
                key={index}
              >
                <Avatar
                  className={classes.avatar}
                  alt="backspace"
                  src={backspaceLogo}
                >
                  b
                </Avatar>
                <div>
                  <Typography className={classes.primaryText} variant="body2">
                    {primaryText}
                  </Typography>
                  <div className={classes.secondaryText}>
                    <Typography variant="caption" color="primary">
                      <b>{secondaryText}</b>
                    </Typography>
                  </div>
                </div>
              </RRLink>
            );
          })
        ) : (
          <div className={classes.fileNotFound}>{t('notifications.itemNotFound')}</div>
        )}
        {actionItems.count > ACTION_LIMIT && (
          <Button
            className={classes.callToActionButton}
            component={RRLink}
            to="/my-tasks"
            onClick={handleNotification(false)}
            color="primary"
            variant="outlined"
            size="small"
            fullWidth
          >
            {t('notifications.seeMore')}
          </Button>
        )}
      </Menu>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fileNotFound: {
      padding: theme.spacing(2, 2, 4, 2),
      textAlign: 'center'
    },
    countBadge: {
      minWidth: 20,
      height: 20,
      padding: theme.spacing(0, 1),
      margin: theme.spacing(0, 0.5, 0, 0),
      borderRadius: 10,
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.secondary.main,
      textAlign: 'center'
    },
    notiButton: {
      [theme.breakpoints.down('xs')]: {
        marginRight: theme.spacing(-0.5)
      }
    },
    notiHeader: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(0, 1.5, 0.5, 2)
    },
    notiItem: {
      display: 'flex',
      padding: theme.spacing(1, 1, 0.5, 2),
      cursor: 'pointer',
      textDecoration: 'none',
      color: theme.palette.text.primary,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: fade(theme.palette.text.primary, 0.04)
      }
    },
    avatar: { width: '32px', height: '32px' },
    primaryText: {
      padding: theme.spacing(0, 1),
      lineHeight: '120%',
      display: '-webkit-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    },
    secondaryText: {
      padding: theme.spacing(0.5, 1, 0, 1),
      textAlign: 'right',
      '* > &': {
        lineHeight: '100%'
      }
    },
    callToActionButton: {
      margin: theme.spacing(0, 1),
      width: `calc(100% - ${theme.spacing(2)}px)`
    },
    flexGrow: {
      flexGrow: 1
    }
  })
);
